/**
 * How the CRM shows and uses an app view's rules: the words on a read-only chip for each fixed filter, and the fixed
 * filters a board filter control stands for once the reader picks a value.
 */
import { describeViewFilter, type AppViewFilter } from '@diggens/types/apps';

export interface RuleFieldInfo {
  key: string;
  label: string;
  type: string;
  picklistName?: string;
}

export interface ChipLabelSources {
  fields: ReadonlyArray<RuleFieldInfo>;
  /** Picklist name to item id to item label. */
  picklistLabels: Record<string, Record<string, string>>;
  /** User id to display name. */
  userNames: Record<string, string>;
}

const SYSTEM_FIELD_LABELS: Record<string, string> = { created_at: 'Created', updated_at: 'Updated' };

/** A fixed filter in the reader's words: field label, picklist item labels and user names instead of stored ids. */
export function fixedFilterChipLabel(filter: AppViewFilter, sources: ChipLabelSources): string {
  const field = sources.fields.find((candidate) => candidate.key === filter.field);
  const fieldLabel = field?.label ?? SYSTEM_FIELD_LABELS[filter.field] ?? filter.field;
  const picklist = field?.picklistName ? sources.picklistLabels[field.picklistName] : undefined;
  const valueLabel = (value: string): string => {
    if (picklist) return picklist[value] ?? value;
    if (field?.type === 'user') return sources.userNames[value] ?? value;
    return value;
  };
  return describeViewFilter(filter, { fieldLabel, valueLabel });
}

/** The value of a board filter control: "any" (no filter), "none" (the field is empty), a stored id, or a date preset. */
export const BOARD_FILTER_ANY = '__any__';
export const BOARD_FILTER_NONE = '__none__';

export const DATE_BOARD_PRESETS = [
  { value: 'overdue', label: 'Before today' },
  { value: 'today', label: 'Today' },
  { value: 'next7', label: 'Next 7 days' },
  { value: 'past7', label: 'Last 7 days' },
] as const;
export type DateBoardPreset = (typeof DATE_BOARD_PRESETS)[number]['value'];

/**
 * The fixed filters one board control adds to the request for the value the reader picked. Select and user fields
 * filter by the chosen id (or by emptiness); date fields filter by a preset expressed as before/after relative days,
 * so the API resolves them in the reader's time zone like any other fixed filter.
 */
export function boardFilterToViewFilters(field: RuleFieldInfo, value: string): AppViewFilter[] {
  if (value === BOARD_FILTER_ANY || value === '') return [];
  if (value === BOARD_FILTER_NONE) return [{ field: field.key, op: 'isEmpty' }];
  if (field.type === 'date' || field.type === 'datetime') {
    switch (value as DateBoardPreset) {
      case 'overdue':
        return [{ field: field.key, op: 'before', value: 'today' }];
      case 'today':
        return [
          { field: field.key, op: 'after', value: '-1d' },
          { field: field.key, op: 'before', value: '+1d' },
        ];
      case 'next7':
        return [
          { field: field.key, op: 'after', value: '-1d' },
          { field: field.key, op: 'before', value: '+8d' },
        ];
      case 'past7':
        return [
          { field: field.key, op: 'after', value: '-8d' },
          { field: field.key, op: 'before', value: 'today' },
        ];
      default:
        return [];
    }
  }
  return [{ field: field.key, op: 'eq', value }];
}
